// src/components/layout/MainLayout.js
import React, { useState, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import './MainLayout.css';
import Navbar from './Navbar';
import Footer from './Footer';
import ScrollingLogos from '../common/ScrollingLogos';
import Breadcrumbs from '../common/Breadcrumbs';
import NotificationBar from '../common/NotificationBar';
import TaglineBar from '../common/TaglineBar';
import pb from '../../lib/pocketbase';
import AuthModal from '../auth/AuthModal';
import SiteOnboarding from './SiteOnboarding';
import SeasonalEffects from '../common/SeasonalEffects';
import { useAuth } from '../../contexts/AuthContext';
import confetti from 'canvas-confetti';
import { useNavigate, useLocation } from 'react-router-dom';

function MainLayout() {
  const [promoMessages, setPromoMessages] = React.useState(null);
  const [tagline, setTagline] = React.useState(null);
  const [taglines, setTaglines] = React.useState(null);
  const navbarRef = React.useRef(null);
  const topChromeRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // --- GESTION DES NOTIFICATIONS ---
  const [notifications, setNotifications] = useState([
    { id: 'onboarding-theme', title: '🎨 Personnaliser l\'affichage', time: 'Configuration', read: false, type: 'action', action: 'theme' },
    { id: 'onboarding-account', title: '🚀 Finaliser votre inscription', time: 'Important', read: false, type: 'action', action: 'account' }
  ]);

  // --- GESTION DU THÈME ---
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark' || saved === 'light') return saved;
    } catch { }
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  const didMountRef = useRef(false);
  useEffect(() => {
    document.body.classList.toggle('dark', theme === 'dark');
    if (didMountRef.current) {
      try { localStorage.setItem('theme', theme); } catch { }
    } else {
      didMountRef.current = true;
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  // Sync with other tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'theme') {
        const val = e.newValue === 'dark' ? 'dark' : 'light';
        if (val !== theme) setTheme(val);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [theme]);


  // --- GESTION DE LA CÉLÉBRATION DE PROMOTION ---
  const { user } = useAuth();
  const prevRoleRef = useRef(user?.role);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showDemotionModal, setShowDemotionModal] = useState(false);
  const [promotedRole, setPromotedRole] = useState(null);

  useEffect(() => {
    // Si l'utilisateur passe de 'user' à 'commercial' ou 'developpeur'
    const prevRole = prevRoleRef.current;

    // On vérifie que ce n'est pas le premier chargement (prevRole undefined -> role defined)
    // On veut seulement les changements explicites depuis "user"
    if (prevRole === 'user' && (user?.role === 'commercial' || user?.role === 'developpeur')) {
      setPromotedRole(user.role);
      setShowPromotionModal(true);
      // Confetti is now triggered by the modal visibility effect below.
    } else if ((prevRole === 'commercial' || prevRole === 'developpeur') && user?.role === 'user') {
      // Demotion detected
      setShowDemotionModal(true);
    }
    prevRoleRef.current = user?.role;
  }, [user?.role]);

  // Continuous Confetti Effect when Modal is open
  useEffect(() => {
    if (!showPromotionModal) return;

    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };
    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    // Trigger immediately
    const runConfetti = () => {
      confetti(Object.assign({}, defaults, { particleCount: 40, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
      confetti(Object.assign({}, defaults, { particleCount: 40, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
    };
    runConfetti();

    const interval = setInterval(runConfetti, 250);

    return () => clearInterval(interval);
  }, [showPromotionModal]);

  // Ensure Welcome notification is always present for staff so they can replay it
  useEffect(() => {
    if (user?.role === 'commercial' || user?.role === 'developpeur') {
      setNotifications(prev => {
        if (prev.find(n => n.action === 'replay_promotion')) return prev; // Already exists
        return [
          {
            id: 'welcome-staff-replay',
            title: `🎉 Bienvenue ! (Rejouer l'intro)`,
            time: 'Espace Staff',
            read: false,
            type: 'action',
            action: 'replay_promotion',
            icon: '🚀'
          },
          ...prev
        ]
      });
    }
  }, [user?.role]);

  // --- GESTION DE L'AUTHENTIFICATION ---
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register' | 'reset'

  const handleOpenAuth = (mode = 'login') => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  // Écouter l'événement global pour ouvrir la modale (depuis le bouton flottant par exemple)
  useEffect(() => {
    const onOpenAuth = (e) => {
      handleOpenAuth(e.detail?.mode || 'login');
    };
    window.addEventListener('open-auth-modal', onOpenAuth);
    return () => window.removeEventListener('open-auth-modal', onOpenAuth);
  }, []);

  // --- GESTION DU REPLAY ONBOARDING (VIA NOTIFICATIONS) ---
  const [manualOnboardingStep, setManualOnboardingStep] = useState(null);

  const handleTriggerOnboarding = (step) => {
    if (step === 'replay_promotion') {
      setShowPromotionModal(true);
    } else {
      setManualOnboardingStep(step);
    }
  };


  React.useEffect(() => {
    let canceled = false;
    const load = async () => {
      try {
        const res = await pb.collection('site_announcements').getList(1, 50, { sort: 'sort_order, -created', filter: 'is_active = true' });
        const items = (res?.items ?? []).map((it) => ({ id: it.id, text: it.text || '' })).filter(it => it.text);
        if (!canceled) setPromoMessages(items);
      } catch {
        if (!canceled) setPromoMessages(null);
      }
      try {
        const res = await pb.collection('site_taglines').getList(1, 50, { sort: 'sort_order, -created', filter: 'is_active = true' });
        const items = (res?.items ?? []).map((it) => String(it?.text || '').trim()).filter(Boolean);
        if (!canceled) setTaglines(items.length ? items : null);
      } catch {
        if (!canceled) setTaglines(null);
      }
      try {
        const res = await pb.collection('site_site_settings').getList(1, 1, { sort: '-created' });
        const it = res?.items?.[0] || null;
        if (!canceled) setTagline(it?.tagline || null);
      } catch {
        if (!canceled) setTagline(null);
      }
    };
    load();
    return () => { canceled = true; };
  }, []);

  React.useLayoutEffect(() => {
    const navEl = navbarRef.current;
    const chromeEl = topChromeRef.current;
    if (!navEl && !chromeEl) return;
    const apply = () => {
      const navH = navEl?.offsetHeight || 0;
      const chromeH = chromeEl?.offsetHeight || 0;
      document.documentElement.style.setProperty('--top-chrome-height', `${navH + chromeH}px`);
      document.documentElement.style.setProperty('--nav-height', `${navH}px`);
    };
    apply();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => apply());
      if (navEl) ro.observe(navEl);
      if (chromeEl) ro.observe(chromeEl);
    }
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      if (ro) ro.disconnect();
    };
  }, []);

  const customTagline = "INFO COLAB : Votre Vision. Notre Code. Votre Succès.";
  const taglineMessages = (taglines && taglines.length)
    ? taglines
    : [(tagline || customTagline)].filter(Boolean);

  return (
    <div className="main-layout">
      {/* Composant d'Onboarding pour les nouveaux visiteurs (Thème + Compte) */}
      <SiteOnboarding
        theme={theme}
        setTheme={setTheme}
        openAuthModal={handleOpenAuth}
        manualStep={manualOnboardingStep}
        onManualClose={() => setManualOnboardingStep(null)}
      />
      <SeasonalEffects />

      <div ref={navbarRef} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1002 }}>
        {/* On passe les props pour contrôler le thème et l'auth depuis ici */}
        <Navbar
          theme={theme}
          toggleTheme={toggleTheme}
          openAuthModal={handleOpenAuth}
          triggerOnboarding={handleTriggerOnboarding}
          notifications={notifications}
          setNotifications={setNotifications}
        />
      </div>
      <div className="navbar-spacer" />
      <div ref={topChromeRef}>
        <NotificationBar messages={promoMessages || undefined} intervalMs={5000} />
        <TaglineBar taglines={taglineMessages} intervalMs={5000} tagline={tagline || customTagline} />
        <ScrollingLogos />
        <Breadcrumbs />
      </div>
      <main className="main-content" style={{ paddingBottom: location.pathname.startsWith('/mon-compte') ? 0 : 20 }}>
        <Outlet />
      </main>

      {!location.pathname.startsWith('/mon-compte') && <Footer />}

      {/* Modale d'authentification globale */}
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />

      {/* Modal de Célébration de Promotion */}
      {showPromotionModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            background: theme === 'dark' ? '#1f2937' : 'white',
            color: theme === 'dark' ? 'white' : 'black',
            padding: '40px', borderRadius: '24px', textAlign: 'center',
            maxWidth: '500px', width: '90%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '2px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #facc15, #a855f7, #3b82f6)',
            backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowPromotionModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'transparent',
                border: 'none',
                fontSize: '1.25rem',
                cursor: 'pointer',
                color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.4)',
                padding: '8px',
                lineHeight: 1,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                transition: 'background 0.2s',
                width: '36px',
                height: '36px'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              aria-label="Fermer"
            >
              ✕
            </button>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🎉</div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '10px', background: 'linear-gradient(to right, #facc15, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Félicitations !</h2>
            <p style={{ fontSize: '1.2rem', marginBottom: '20px', opacity: 0.9 }}>
              Votre candidature a été validée avec succès.
            </p>
            <p style={{ marginBottom: '30px', fontSize: '1.1rem' }}>
              Bienvenue dans l'équipe en tant que <strong style={{ color: '#3b82f6', textTransform: 'capitalize' }}>{promotedRole}</strong> !<br />
              Votre espace a été mis à jour instantanément.
            </p>
            <button
              onClick={() => {
                setShowPromotionModal(false);
                navigate('/commercial/launchpad');
              }}
              className="btn"
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white', padding: '12px 32px', borderRadius: '12px',
                border: 'none', fontWeight: 700, fontSize: '1.1rem', cursor: 'pointer',
                boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)'
              }}
            >
              Accéder à mon nouvel espace 🚀
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MainLayout;